import { doc, runTransaction, type Firestore, type Transaction } from 'firebase/firestore';
import type { Expense } from '../types';

/** A deterministic, retained claim lets rules reject old clients' extra charges. */
export function claimFullCost(transaction: Transaction, db: Firestore, tripId: string, expense: Expense): Expense {
  if (expense.amountBasis !== 'full-cost') return expense;
  const sourceType = expense.sourceType === 'note' ? 'other' : expense.sourceType;
  if (!sourceType || !expense.sourceId || expense.sourceId.includes('/')) throw new Error('A full booking cost needs a valid item link.');
  transaction.set(doc(db, 'trips', tripId, 'bookingCosts', `${sourceType}:${expense.sourceId}`), {
    expenseId: expense.id, sourceId: expense.sourceId, sourceType,
  });
  return { ...expense, fullCostRevision: 1 };
}

/** Retain the guard even after a failed review; concurrent reviews must never unlock one another. */
export async function reserveLegacyCostReview(db: Firestore, tripId: string, expense: Expense): Promise<void> {
  const sourceType = expense.sourceType === 'note' ? 'other' : expense.sourceType;
  if (!sourceType || !expense.sourceId || expense.sourceId.includes('/')) throw new Error('Review the item link first.');
  const ref = doc(db, 'trips', tripId, 'bookingCosts', `${sourceType}:${expense.sourceId}`);
  await runTransaction(db, async tx => {
    const saved = await tx.get(doc(db, 'trips', tripId, 'expenses', expense.id));
    const claim = await tx.get(ref);
    if (!saved.exists() || saved.data().amountBasis || saved.data().sourceId !== expense.sourceId
      || saved.data().sourceType !== expense.sourceType || (claim.exists() && claim.data().expenseId !== expense.id)) {
      throw new Error('This legacy booking changed. Reopen it before reviewing its cost.');
    }
    if (!claim.exists()) tx.set(ref, { expenseId: expense.id, sourceId: expense.sourceId, sourceType });
  });
}
